import { LitElement, html, css, nothing } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import uPlot from 'uplot'
import uPlotCss from 'uplot/dist/uPlot.min.css?inline'
import type { HourlyForecast, EnsembleSpread } from '../../services/weather/weather-types.js'
import { capeToLightningRisk } from '../../services/weather/weather-utils.js'
import type { UnitSystem } from '../../utils/units.js'
import type { PlanTimeTag } from '../../schemas/plan.js'

export interface ChartSource {
  id: string
  label: string
  hourly: HourlyForecast[]
  color: string
}

export interface ChartPlanTime {
  datetime: string
  icon: string
  tag: PlanTimeTag
}

@customElement('weather-day-chart')
export class WeatherDayChart extends LitElement {
  static override styles = css`
    :host {
      display: block;
      width: 100%;
    }

    .chart-section {
      position: relative;
      width: 100%;
      height: 120px;
      margin-bottom: 0;
      overflow: hidden;
    }

    .chart-section.precip {
      height: 120px;
    }

    .chart-label {
      position: absolute;
      top: 2px;
      left: 2px;
      font-size: 10px;
      font-weight: 700;
      color: rgba(255, 255, 255, 0.5);
      z-index: 5;
      pointer-events: none;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .legend {
      display: flex;
      flex-wrap: wrap;
      gap: 8px 14px;
      padding: 4px 0 8px;
      font-size: 11px;
    }

    .legend-item {
      display: flex;
      align-items: center;
      gap: 4px;
      color: rgba(255, 255, 255, 0.8);
    }

    .legend-swatch {
      width: 12px;
      height: 3px;
      border-radius: 1px;
    }

    .legend-swatch.dashed {
      background: repeating-linear-gradient(
        90deg,
        var(--swatch-color) 0px,
        var(--swatch-color) 4px,
        transparent 4px,
        transparent 7px
      );
    }

    .legend-swatch.band {
      height: 8px;
      opacity: 0.4;
      border-radius: 2px;
    }

    .lightning-row {
      display: flex;
      align-items: center;
      gap: 4px;
      margin: 2px 0 8px;
    }

    .lightning-label {
      font-size: 12px;
      flex-shrink: 0;
      width: 55px;
      text-align: right;
      padding-right: 4px;
    }

    .lightning-strip {
      display: flex;
      height: 6px;
      border-radius: 3px;
      overflow: hidden;
      flex: 1;
    }

    .lightning-cell {
      flex: 1;
    }

    .chart-section .cursor-tooltip {
      position: absolute;
      top: 4px;
      right: 8px;
      background: rgba(0, 0, 0, 0.7);
      color: white;
      font-size: 11px;
      padding: 2px 6px;
      border-radius: 3px;
      pointer-events: none;
      z-index: 20;
      white-space: nowrap;
    }

    .tooltip-time {
      font-weight: 600;
      margin-bottom: 2px;
    }

    .tooltip-row {
      display: flex;
      align-items: center;
      gap: 4px;
    }

    .tooltip-swatch {
      width: 12px;
      height: 3px;
      border-radius: 1px;
      flex-shrink: 0;
    }

    .tooltip-swatch.band {
      height: 8px;
      opacity: 0.4;
      border-radius: 2px;
    }

    .no-data {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 80px;
      opacity: 0.5;
      font-size: 13px;
    }
  `

  private static readonly TAG_COLORS: Record<string, string> = {
    meeting: '#3b82f6',
    start: '#22c55e',
    turnaround: '#f59e0b',
    return: '#3b82f6',
    overdue: '#ef4444',
    custom: '#64748b',
  }

  @property({ type: Array }) sources: ChartSource[] = []
  @property({ type: Array }) ensembleSpreads: EnsembleSpread[] = []
  @property({ type: Array }) planTimes: ChartPlanTime[] = []
  @property({ type: String }) unitSystem: UnitSystem = 'imperial'
  @property({ type: String }) sunrise = ''
  @property({ type: String }) sunset = ''

  private tempChart: uPlot | undefined
  private precipChart: uPlot | undefined
  private windChart: uPlot | undefined
  private stylesInjected = false
  private _syncing = false
  private _mouseInside = false
  private _syncingZoom = false
  private _trackingSetup = false

  @state() private tempTooltip: ReturnType<typeof html> | null = null
  @state() private precipTooltip: ReturnType<typeof html> | null = null
  @state() private windTooltip: ReturnType<typeof html> | null = null

  private cursorHours: number[] = []
  private cursorSeries: {
    label: string
    color: string
    temp: (number | null)[]
    precip: (number | null)[]
    wind: (number | null)[]
  }[] = []
  private cursorEnsemble: {
    temp: { min: number | null; max: number | null }[]
    precip: { min: number | null; max: number | null }[]
    wind: { min: number | null; max: number | null }[]
  } | null = null

  private themeColors = {
    axisStroke: 'rgba(255,255,255,0.6)',
    gridStroke: 'rgba(255,255,255,0.15)',
    cursor: 'rgba(255,255,255,0.5)',
  }

  private readThemeColors(): void {
    const s = getComputedStyle(this)
    this.themeColors = {
      axisStroke:
        s.getPropertyValue('--cairn-text-secondary-color').trim() || this.themeColors.axisStroke,
      gridStroke:
        s.getPropertyValue('--cairn-border-color-light').trim() || this.themeColors.gridStroke,
      cursor: s.getPropertyValue('--cairn-text-muted-color').trim() || this.themeColors.cursor,
    }
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback()
    this.destroyCharts()
  }

  override updated(changed: Map<string, unknown>): void {
    if (
      changed.has('sources') ||
      changed.has('ensembleSpreads') ||
      changed.has('unitSystem') ||
      changed.has('planTimes')
    ) {
      this.rebuildCharts()
    }
  }

  override firstUpdated(): void {
    this.ensureStyles()
    requestAnimationFrame(() => this.rebuildCharts())
  }

  private ensureStyles(): void {
    if (!this.stylesInjected && this.shadowRoot) {
      this.readThemeColors()
      const style = document.createElement('style')
      style.textContent =
        uPlotCss +
        `\n.u-cursor-x { border-left: 1px solid ${this.themeColors.cursor} !important; }` +
        `\n.u-cursor-pt { display: none !important; }` +
        `\n.u-legend { display: none !important; }` +
        `\n.u-select { background: rgba(255,255,255,0.1) !important; }`
      this.shadowRoot.appendChild(style)
      this.stylesInjected = true
    }
  }

  private destroyCharts(): void {
    this.tempChart?.destroy()
    this.windChart?.destroy()
    this.precipChart?.destroy()
    this.tempChart = undefined
    this.windChart = undefined
    this.precipChart = undefined
    this.tempTooltip = null
    this.precipTooltip = null
    this.windTooltip = null
    this.cursorHours = []
    this.cursorSeries = []
    this.cursorEnsemble = null
    this._mouseInside = false
  }

  private get primarySource(): ChartSource | undefined {
    return this.sources[0]
  }

  private withAlpha(color: string, alpha: number): string {
    if (color.startsWith('#')) {
      const r = parseInt(color.slice(1, 3), 16)
      const g = parseInt(color.slice(3, 5), 16)
      const b = parseInt(color.slice(5, 7), 16)
      return `rgba(${r},${g},${b},${alpha})`
    }
    return color.replace(')', `,${alpha})`).replace('rgb', 'rgba')
  }

  private niceNum(val: number): number {
    if (val <= 0) return 1
    const exp = Math.floor(Math.log10(val))
    const frac = val / Math.pow(10, exp)
    const nice = frac < 1.5 ? 1 : frac < 3 ? 2 : frac < 7 ? 5 : 10
    return nice * Math.pow(10, exp)
  }

  private fewSplits() {
    return (_u: uPlot, _axisIdx: number, min: number, max: number): number[] => {
      const range = max - min
      if (range <= 0) return [min]
      const step = this.niceNum(range / 2)
      const start = Math.ceil(min / step) * step
      const vals: number[] = []
      for (let v = start; v <= max + step * 0.0001; v += step) {
        vals.push(parseFloat((Math.round(v / step) * step).toPrecision(12)))
      }
      return vals
    }
  }

  private rebuildCharts(): void {
    this.destroyCharts()
    if (this.sources.length === 0) return

    const timeSource = this.sources.find(s => s.hourly.length > 0)
    if (!timeSource) return

    const isMetric = this.unitSystem === 'metric'
    const hours = timeSource.hourly.map(h => new Date(h.time).getTime() / 1000)

    const tempConv = isMetric ? (f: number) => ((f - 32) * 5) / 9 : (f: number) => f
    const windConv = isMetric ? (mph: number) => mph * 1.60934 : (mph: number) => mph
    const precipConv = isMetric ? (inch: number) => inch * 25.4 : (inch: number) => inch

    const sunriseTs = this.sunrise ? new Date(this.sunrise).getTime() / 1000 : 0
    const sunsetTs = this.sunset ? new Date(this.sunset).getTime() / 1000 : 0

    const planTimeMarkers = this.planTimes.map(pt => ({
      ts: new Date(pt.datetime).getTime() / 1000,
      icon: pt.icon,
      color: WeatherDayChart.TAG_COLORS[pt.tag] ?? '#64748b',
    }))

    const spreadByHour = new Map<number, EnsembleSpread>()
    if (this.ensembleSpreads.length > 0) {
      const sorted = [...this.ensembleSpreads].sort(
        (a, b) => new Date(a.time).getTime() - new Date(b.time).getTime()
      )
      let last: EnsembleSpread | undefined
      for (let h = 0; h < 24; h++) {
        const exact = sorted.find(s => new Date(s.time).getHours() === h)
        if (exact) last = exact
        if (last) spreadByHour.set(h, last)
      }
    }

    // DEBUG: ensemble matching
    console.group('[weather-day-chart] rebuild')
    console.log(
      'sources:',
      this.sources.map(s => ({ id: s.id, label: s.label, hours: s.hourly.length }))
    )
    console.log('ensembleSpreads received:', this.ensembleSpreads.length)
    console.log('spreadByHour mapped hours:', [...spreadByHour.keys()])
    if (spreadByHour.size > 0) {
      const sample = spreadByHour.values().next().value
      console.log('sample spread values:', sample)
    }
    console.log(
      'chart hours (first 6):',
      hours.slice(0, 6).map(t => new Date(t * 1000).toISOString())
    )
    console.groupEnd()

    this.cursorHours = hours
    this.cursorSeries = this.sources.map(src => {
      const aligned = this.alignSource(src, hours)
      return {
        label: src.label,
        color: src.color,
        temp: hours.map(t => {
          const h = aligned.get(t)
          return h != null ? tempConv(h.temperature) : null
        }),
        precip: hours.map(t => {
          const h = aligned.get(t)
          return h != null ? precipConv(h.precipAmount + h.snowfall) : null
        }),
        wind: hours.map(t => {
          const h = aligned.get(t)
          return h != null ? windConv(h.windSpeed) : null
        }),
      }
    })

    if (spreadByHour.size > 0) {
      this.cursorEnsemble = {
        temp: hours.map(t => {
          const h = new Date(t * 1000).getHours()
          const s = spreadByHour.get(h)
          return s
            ? { min: tempConv(s.temperature.min), max: tempConv(s.temperature.max) }
            : { min: null, max: null }
        }),
        precip: hours.map(t => {
          const h = new Date(t * 1000).getHours()
          const s = spreadByHour.get(h)
          return s
            ? { min: precipConv(s.precipitation.min), max: precipConv(s.precipitation.max) }
            : { min: null, max: null }
        }),
        wind: hours.map(t => {
          const h = new Date(t * 1000).getHours()
          const s = spreadByHour.get(h)
          return s
            ? { min: windConv(s.wind.min), max: windConv(s.wind.max) }
            : { min: null, max: null }
        }),
      }
    } else {
      this.cursorEnsemble = null
    }

    this.buildTempChart(
      hours,
      tempConv,
      spreadByHour,
      sunriseTs,
      sunsetTs,
      isMetric,
      planTimeMarkers
    )
    this.buildPrecipChart(
      hours,
      precipConv,
      spreadByHour,
      sunriseTs,
      sunsetTs,
      isMetric,
      planTimeMarkers
    )
    this.buildWindChart(
      hours,
      windConv,
      spreadByHour,
      sunriseTs,
      sunsetTs,
      isMetric,
      planTimeMarkers
    )

    if (!this._trackingSetup) {
      this.setupCursorTracking()
      this._trackingSetup = true
    }
  }

  private timeAxis(): uPlot.Axis {
    return {
      stroke: this.themeColors.axisStroke,
      grid: { show: false },
      ticks: { show: false },
      font: '10px sans-serif',
      values: (_u: uPlot, vals: number[]) =>
        vals.map(v => {
          const h = new Date(v * 1000).getHours()
          return h === 0 ? '12a' : h < 12 ? `${h}a` : h === 12 ? '12p' : `${h - 12}p`
        }),
    }
  }

  private static readonly ICON_PAD = 36

  private baseOpts(
    container: HTMLElement,
    sunriseTs: number,
    sunsetTs: number,
    planTimeMarkers: { ts: number; icon: string; color: string }[] = []
  ): uPlot.Options {
    const hasMarkers = sunriseTs > 0 || sunsetTs > 0 || planTimeMarkers.length > 0
    const topPad = hasMarkers ? WeatherDayChart.ICON_PAD : 0
    return {
      width: container.clientWidth,
      height: container.clientHeight,
      padding: [topPad, null, null, null] as unknown as uPlot.Padding,
      cursor: {
        show: true,
        x: true,
        y: false,
        drag: { x: true, y: false, setScale: true },
      },
      legend: { show: false },
      axes: [
        this.timeAxis(),
        {
          stroke: this.themeColors.axisStroke,
          grid: { stroke: this.themeColors.gridStroke, width: 1 },
          ticks: { show: false },
          font: '10px sans-serif',
          size: 55,
        },
      ],
      scales: { x: { auto: true } },
      series: [{}],
      hooks: {
        setCursor: [this.handleCursor],
        setScale: [
          (u: uPlot, key: string) => {
            if (key === 'x' && !this._syncingZoom) {
              this._syncingZoom = true
              for (const chart of [this.tempChart, this.precipChart, this.windChart]) {
                if (chart && chart !== u)
                  chart.setScale('x', { min: u.scales.x.min!, max: u.scales.x.max! })
              }
              this._syncingZoom = false
            }
          },
        ],
        draw: [
          (u: uPlot) => {
            const ctx = u.ctx
            const sunMarkers: { ts: number; label: string }[] = []
            if (sunriseTs) sunMarkers.push({ ts: sunriseTs, label: '☀↑' })
            if (sunsetTs) sunMarkers.push({ ts: sunsetTs, label: '☀↓' })
            for (const s of sunMarkers) {
              const px = u.valToPos(s.ts, 'x', true)
              if (px < u.bbox.left || px > u.bbox.left + u.bbox.width) continue
              ctx.save()
              ctx.strokeStyle = 'rgba(255,200,50,0.3)'
              ctx.lineWidth = 1
              ctx.setLineDash([3, 3])
              ctx.beginPath()
              ctx.moveTo(px, u.bbox.top)
              ctx.lineTo(px, u.bbox.top + u.bbox.height)
              ctx.stroke()
              ctx.restore()
              ctx.save()
              ctx.font = '20px sans-serif'
              ctx.fillStyle = 'rgba(255,200,50,0.8)'
              ctx.textAlign = 'center'
              ctx.textBaseline = 'bottom'
              ctx.fillText(s.label, px, u.bbox.top - 4)
              ctx.restore()
            }
            for (const m of planTimeMarkers) {
              const px = u.valToPos(m.ts, 'x', true)
              if (px < u.bbox.left || px > u.bbox.left + u.bbox.width) continue
              ctx.save()
              ctx.strokeStyle = m.color + '66'
              ctx.lineWidth = 1
              ctx.setLineDash([3, 3])
              ctx.beginPath()
              ctx.moveTo(px, u.bbox.top)
              ctx.lineTo(px, u.bbox.top + u.bbox.height)
              ctx.stroke()
              ctx.restore()
              ctx.save()
              ctx.font = '28px "Material Symbols Outlined"'
              ctx.fillStyle = m.color
              ctx.textAlign = 'center'
              ctx.textBaseline = 'bottom'
              ctx.fillText(m.icon, px, u.bbox.top - 2)
              ctx.restore()
            }
          },
        ],
      },
    }
  }

  private alignSource(source: ChartSource, _primaryTimes: number[]): Map<number, HourlyForecast> {
    const byHour = new Map<number, HourlyForecast>()
    for (const h of source.hourly) {
      const ts = new Date(h.time).getTime() / 1000
      byHour.set(ts, h)
    }
    return byHour
  }

  private buildTempChart(
    hours: number[],
    tempConv: (f: number) => number,
    spreadByHour: Map<number, EnsembleSpread>,
    sunriseTs: number,
    sunsetTs: number,
    isMetric: boolean,
    planTimeMarkers: { ts: number; icon: string; color: string }[]
  ): void {
    const el = this.shadowRoot?.querySelector('.chart-temp') as HTMLElement | null
    if (!el) return

    const opts = this.baseOpts(el, sunriseTs, sunsetTs, planTimeMarkers)
    const unit = isMetric ? '°C' : '°F'
    opts.axes![1].values = (_u: uPlot, vals: number[]) => vals.map(v => `${Math.round(v)}${unit}`)
    opts.axes![1].splits = this.fewSplits()
    opts.scales!.y = {
      auto: true,
      range: (_u, min, max) => {
        const span = max - min
        if (span < 10) {
          const mid = (min + max) / 2
          return [Math.floor(mid - 5), Math.ceil(mid + 5)]
        }
        return [min, max]
      },
    }

    const data: (number | null)[][] = [hours]

    for (const src of this.sources) {
      const aligned = this.alignSource(src, hours)
      opts.series.push({
        label: src.label,
        stroke: src.color,
        width: 2,
      })
      data.push(
        hours.map(t => {
          const h = aligned.get(t)
          return h != null ? tempConv(h.temperature) : null
        })
      )
    }

    if (spreadByHour.size > 0) {
      opts.series.push({
        label: 'Area',
        stroke: 'rgba(34,197,94,0.6)',
        width: 1,
        dash: [4, 4],
        points: { show: false },
      })
      data.push(
        hours.map(t => {
          const h = new Date(t * 1000).getHours()
          const s = spreadByHour.get(h)
          return s ? tempConv(s.temperature.median) : null
        })
      )

      const minIdx = opts.series.length
      opts.series.push({ label: 'min', stroke: 'transparent', width: 0, points: { show: false } })
      const maxIdx = opts.series.length
      opts.series.push({ label: 'max', stroke: 'transparent', width: 0, points: { show: false } })
      data.push(
        hours.map(t => {
          const h = new Date(t * 1000).getHours()
          const s = spreadByHour.get(h)
          return s ? tempConv(s.temperature.min) : null
        }),
        hours.map(t => {
          const h = new Date(t * 1000).getHours()
          const s = spreadByHour.get(h)
          return s ? tempConv(s.temperature.max) : null
        })
      )
      opts.bands = [{ series: [maxIdx, minIdx] as uPlot.Band.Bounds, fill: 'rgba(34,197,94,0.2)' }]
    }

    this.tempChart = new uPlot(opts, data as uPlot.AlignedData, el)
  }

  private buildPrecipChart(
    hours: number[],
    precipConv: (inch: number) => number,
    spreadByHour: Map<number, EnsembleSpread>,
    sunriseTs: number,
    sunsetTs: number,
    isMetric: boolean,
    planTimeMarkers: { ts: number; icon: string; color: string }[]
  ): void {
    const el = this.shadowRoot?.querySelector('.chart-precip') as HTMLElement | null
    if (!el) return

    const opts = this.baseOpts(el, sunriseTs, sunsetTs, planTimeMarkers)
    const unit = isMetric ? 'mm' : '″'
    opts.axes![1].values = (_u: uPlot, vals: number[]) =>
      vals.map(v => {
        if (v === 0) return `0${unit}`
        if (isMetric) return `${v.toFixed(v < 1 ? 1 : 0)}${unit}`
        return `${v < 0.1 ? v.toFixed(2) : v.toFixed(1)}${unit}`
      })
    opts.axes![1].splits = this.fewSplits()
    opts.scales!.y = {
      auto: true,
      range: (_u, _min, max) => [0, Math.max(max * 1.1, isMetric ? 1 : 0.05)],
    }

    const data: (number | null)[][] = [hours]

    for (const src of this.sources) {
      const aligned = this.alignSource(src, hours)
      opts.series.push({
        label: src.label,
        stroke: src.color,
        fill: this.withAlpha(src.color, 0.3),
        width: 0,
        paths: uPlot.paths.bars!({ size: [0.6 / this.sources.length, 40] }),
      })
      data.push(
        hours.map(t => {
          const h = aligned.get(t)
          return h != null ? precipConv(h.precipAmount + h.snowfall) : null
        })
      )
    }

    if (spreadByHour.size > 0) {
      const minIdx = opts.series.length
      opts.series.push({ label: 'min', stroke: 'transparent', width: 0, points: { show: false } })
      const maxIdx = opts.series.length
      opts.series.push({ label: 'max', stroke: 'transparent', width: 0, points: { show: false } })
      data.push(
        hours.map(t => {
          const h = new Date(t * 1000).getHours()
          const s = spreadByHour.get(h)
          return s ? precipConv(s.precipitation.min) : null
        }),
        hours.map(t => {
          const h = new Date(t * 1000).getHours()
          const s = spreadByHour.get(h)
          return s ? precipConv(s.precipitation.max) : null
        })
      )
      opts.bands = [{ series: [maxIdx, minIdx] as uPlot.Band.Bounds, fill: 'rgba(34,197,94,0.2)' }]
    }

    this.precipChart = new uPlot(opts, data as uPlot.AlignedData, el)
  }

  private buildWindChart(
    hours: number[],
    windConv: (mph: number) => number,
    spreadByHour: Map<number, EnsembleSpread>,
    sunriseTs: number,
    sunsetTs: number,
    isMetric: boolean,
    planTimeMarkers: { ts: number; icon: string; color: string }[]
  ): void {
    const el = this.shadowRoot?.querySelector('.chart-wind') as HTMLElement | null
    if (!el) return

    const opts = this.baseOpts(el, sunriseTs, sunsetTs, planTimeMarkers)
    const unit = isMetric ? 'km/h' : 'mph'
    opts.axes![1].values = (_u: uPlot, vals: number[]) => vals.map(v => `${Math.round(v)} ${unit}`)
    opts.axes![1].splits = this.fewSplits()
    opts.scales!.y = {
      auto: true,
      range: (_u, _min, max) => [0, Math.max(max, isMetric ? 10 : 5)],
    }

    const data: (number | null)[][] = [hours]

    const windDirs: { seriesIdx: number; color: string; dirs: (number | null)[] }[] = []

    for (const src of this.sources) {
      const aligned = this.alignSource(src, hours)
      const seriesIdx = opts.series.length
      opts.series.push({
        label: src.label,
        stroke: src.color,
        width: 2,
        points: { show: false },
      })
      data.push(
        hours.map(t => {
          const h = aligned.get(t)
          return h != null ? windConv(h.windSpeed) : null
        })
      )
      windDirs.push({
        seriesIdx,
        color: src.color,
        dirs: hours.map(t => {
          const h = aligned.get(t)
          return h != null ? h.windDirection : null
        }),
      })
    }

    const drawArrows = (u: uPlot) => {
      const ctx = u.ctx
      const size = 10
      for (const wd of windDirs) {
        const yData = data[wd.seriesIdx]
        if (!yData) continue
        ctx.save()
        ctx.fillStyle = 'white'
        ctx.strokeStyle = wd.color
        ctx.lineWidth = 1.5
        for (let i = 0; i < hours.length; i++) {
          const val = yData[i]
          const dir = wd.dirs[i]
          if (val == null || dir == null) continue
          const cx = u.valToPos(hours[i], 'x', true)
          const cy = u.valToPos(val, 'y', true)
          if (cx < u.bbox.left || cx > u.bbox.left + u.bbox.width) continue
          const rad = (dir * Math.PI) / 180
          ctx.save()
          ctx.translate(cx, cy)
          ctx.rotate(rad)
          ctx.beginPath()
          ctx.moveTo(0, -size)
          ctx.lineTo(-size * 0.6, size * 0.5)
          ctx.lineTo(size * 0.6, size * 0.5)
          ctx.closePath()
          ctx.fill()
          ctx.stroke()
          ctx.restore()
        }
        ctx.restore()
      }
    }

    opts.hooks!.draw = [...(opts.hooks!.draw ?? []), drawArrows]

    if (spreadByHour.size > 0) {
      opts.series.push({
        label: 'Area',
        stroke: 'rgba(34,197,94,0.6)',
        width: 1,
        dash: [4, 4],
        points: { show: false },
      })
      data.push(
        hours.map(t => {
          const h = new Date(t * 1000).getHours()
          const s = spreadByHour.get(h)
          return s ? windConv(s.wind.median) : null
        })
      )

      const minIdx = opts.series.length
      opts.series.push({ label: 'min', stroke: 'transparent', width: 0, points: { show: false } })
      const maxIdx = opts.series.length
      opts.series.push({ label: 'max', stroke: 'transparent', width: 0, points: { show: false } })
      data.push(
        hours.map(t => {
          const h = new Date(t * 1000).getHours()
          const s = spreadByHour.get(h)
          return s ? windConv(s.wind.min) : null
        }),
        hours.map(t => {
          const h = new Date(t * 1000).getHours()
          const s = spreadByHour.get(h)
          return s ? windConv(s.wind.max) : null
        })
      )
      opts.bands = [{ series: [maxIdx, minIdx] as uPlot.Band.Bounds, fill: 'rgba(34,197,94,0.2)' }]
    }

    this.windChart = new uPlot(opts, data as uPlot.AlignedData, el)
  }

  private handleCursor = (u: uPlot): void => {
    if (!this._syncing) {
      this._syncing = true
      const left = u.cursor.left ?? -10
      for (const chart of [this.tempChart, this.precipChart, this.windChart]) {
        if (chart && chart !== u) {
          chart.setCursor({ left, top: -10 }, false)
        }
      }
      this._syncing = false
    }

    const idx = u.cursor.idx
    if (idx == null || !this.cursorHours.length) {
      if (!this._syncing && !this._mouseInside) {
        this.tempTooltip = null
        this.precipTooltip = null
        this.windTooltip = null
      }
      return
    }

    const ts = this.cursorHours[idx]
    const d = new Date(ts * 1000)
    const h = d.getHours()
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h
    const ampm = h < 12 ? 'AM' : 'PM'
    const m = d.getMinutes()
    const time = `${h12}:${m.toString().padStart(2, '0')} ${ampm}`

    const isMetric = this.unitSystem === 'metric'
    const tempUnit = isMetric ? '°C' : '°F'
    const precipUnit = isMetric ? 'mm' : '″'
    const windUnit = isMetric ? 'km/h' : 'mph'
    const ensColor = 'rgba(34,197,94,0.5)'

    const lineRow = (color: string, value: string) =>
      html` <div class="tooltip-row">
        <span class="tooltip-swatch" style="background:${color}"></span>
        ${value}
      </div>`
    const bandRow = (color: string, value: string) =>
      html` <div class="tooltip-row">
        <span class="tooltip-swatch band" style="background:${color}"></span>
        ${value}
      </div>`

    const tempRows = this.cursorSeries
      .filter(s => s.temp[idx] != null)
      .map(s => lineRow(s.color, `${Math.round(s.temp[idx]!)}${tempUnit}`))
    const precipRows = this.cursorSeries
      .filter(s => s.precip[idx] != null)
      .map(s => {
        const v = isMetric ? s.precip[idx]!.toFixed(1) : s.precip[idx]!.toFixed(2)
        return lineRow(s.color, `${v}${precipUnit}`)
      })
    const windRows = this.cursorSeries
      .filter(s => s.wind[idx] != null)
      .map(s => lineRow(s.color, `${Math.round(s.wind[idx]!)} ${windUnit}`))

    if (this.cursorEnsemble) {
      const te = this.cursorEnsemble.temp[idx]
      if (te?.min != null && te?.max != null)
        tempRows.push(bandRow(ensColor, `${Math.round(te.min)}–${Math.round(te.max)}${tempUnit}`))
      const pe = this.cursorEnsemble.precip[idx]
      if (pe?.min != null && pe?.max != null) {
        const pMin = isMetric ? pe.min.toFixed(1) : pe.min.toFixed(2)
        const pMax = isMetric ? pe.max.toFixed(1) : pe.max.toFixed(2)
        precipRows.push(bandRow(ensColor, `${pMin}–${pMax}${precipUnit}`))
      }
      const we = this.cursorEnsemble.wind[idx]
      if (we?.min != null && we?.max != null)
        windRows.push(bandRow(ensColor, `${Math.round(we.min)}–${Math.round(we.max)} ${windUnit}`))
    }

    this.tempTooltip =
      tempRows.length > 0
        ? html`<div class="tooltip-time">${time}</div>
            ${tempRows}`
        : null
    this.precipTooltip =
      precipRows.length > 0
        ? html`<div class="tooltip-time">${time}</div>
            ${precipRows}`
        : null
    this.windTooltip =
      windRows.length > 0
        ? html`<div class="tooltip-time">${time}</div>
            ${windRows}`
        : null
  }

  private getPlotLeft(clientX: number): number | null {
    const ref = this.tempChart
    if (!ref) return null
    const over = ref.root.querySelector('.u-over')
    if (!over) return null
    const rect = over.getBoundingClientRect()
    const left = clientX - rect.left
    return left >= 0 && left <= rect.width ? left : null
  }

  private isOverPlotArea(clientX: number, clientY: number): boolean {
    for (const chart of [this.tempChart, this.precipChart, this.windChart]) {
      if (!chart) continue
      const over = chart.root.querySelector('.u-over') as HTMLElement
      if (!over) continue
      const r = over.getBoundingClientRect()
      if (clientX >= r.left && clientX <= r.right && clientY >= r.top && clientY <= r.bottom)
        return true
    }
    return false
  }

  private resetZoom(): void {
    this._syncingZoom = true
    for (const chart of [this.tempChart, this.precipChart, this.windChart]) {
      if (!chart) continue
      chart.setScale('x', {
        min: chart.data[0][0],
        max: chart.data[0][chart.data[0].length - 1],
      })
    }
    this._syncingZoom = false
  }

  private allCharts(): (uPlot | undefined)[] {
    return [this.tempChart, this.precipChart, this.windChart]
  }

  private setupCursorTracking(): void {
    const wrapper = this.shadowRoot?.querySelector('.charts-wrapper') as HTMLElement
    if (!wrapper) return

    wrapper.addEventListener('mouseenter', () => {
      this._mouseInside = true
    })
    wrapper.addEventListener('mouseleave', () => {
      this._mouseInside = false
      this._syncing = true
      for (const c of this.allCharts()) c?.setCursor({ left: -10, top: -10 }, false)
      this._syncing = false
      this.tempTooltip = null
      this.precipTooltip = null
      this.windTooltip = null
    })

    wrapper.addEventListener('mousemove', (e: MouseEvent) => {
      if (this.isOverPlotArea(e.clientX, e.clientY)) return
      const left = this.getPlotLeft(e.clientX)
      if (left == null) return
      this._syncing = true
      for (const c of this.allCharts()) c?.setCursor({ left, top: -10 })
      this._syncing = false
    })

    wrapper.addEventListener('dblclick', () => this.resetZoom())

    this.setupTouchTracking(wrapper)
  }

  private setupTouchTracking(wrapper: HTMLElement): void {
    const LONG_PRESS_MS = 400
    const MOVE_THRESHOLD = 8
    let pressTimer: ReturnType<typeof setTimeout> | null = null
    let isDragging = false
    let dragStartLeft = 0
    let startX = 0
    let startY = 0
    let lastTap = 0

    wrapper.addEventListener(
      'touchstart',
      (e: TouchEvent) => {
        this._mouseInside = true
        const touch = e.touches[0]
        startX = touch.clientX
        startY = touch.clientY
        const left = this.getPlotLeft(touch.clientX)
        if (left == null) return

        const now = Date.now()
        if (now - lastTap < 300) {
          e.preventDefault()
          lastTap = 0
          this.resetZoom()
          return
        }
        lastTap = now

        isDragging = false
        pressTimer = setTimeout(() => {
          isDragging = true
          dragStartLeft = left
          for (const c of this.allCharts()) {
            if (c) c.setSelect({ left, width: 0, top: 0, height: c.over.clientHeight }, false)
          }
          try {
            const cap = (window as unknown as Record<string, unknown>).Capacitor as
              | { Plugins?: { Haptics?: { impact: (opts: { style: string }) => void } } }
              | undefined
            cap?.Plugins?.Haptics?.impact({ style: 'medium' })
          } catch {
            /* Haptics not available */
          }
        }, LONG_PRESS_MS)

        this._syncing = true
        for (const c of this.allCharts()) c?.setCursor({ left, top: -10 })
        this._syncing = false
      },
      { passive: false }
    )

    wrapper.addEventListener(
      'touchmove',
      (e: TouchEvent) => {
        const touch = e.touches[0]
        const left = this.getPlotLeft(touch.clientX)

        if (
          pressTimer &&
          (Math.abs(touch.clientX - startX) > MOVE_THRESHOLD ||
            Math.abs(touch.clientY - startY) > MOVE_THRESHOLD)
        ) {
          clearTimeout(pressTimer)
          pressTimer = null
        }

        if (isDragging && left != null) {
          e.preventDefault()
          const selLeft = Math.min(dragStartLeft, left)
          const selWidth = Math.abs(left - dragStartLeft)
          for (const c of this.allCharts()) {
            if (c)
              c.setSelect(
                { left: selLeft, width: selWidth, top: 0, height: c.over.clientHeight },
                false
              )
          }
        } else if (left != null) {
          e.preventDefault()
          this._syncing = true
          for (const c of this.allCharts()) c?.setCursor({ left, top: -10 })
          this._syncing = false
        }
      },
      { passive: false }
    )

    wrapper.addEventListener('touchend', () => {
      if (pressTimer) {
        clearTimeout(pressTimer)
        pressTimer = null
      }
      if (isDragging) {
        const ref = this.tempChart
        if (ref) {
          const sel = ref.select
          if (sel.width > 10) {
            const xMin = ref.posToVal(sel.left, 'x')
            const xMax = ref.posToVal(sel.left + sel.width, 'x')
            this._syncingZoom = true
            for (const c of this.allCharts()) {
              if (c) {
                c.setScale('x', { min: xMin, max: xMax })
                c.setSelect({ left: 0, width: 0, top: 0, height: 0 }, false)
              }
            }
            this._syncingZoom = false
          } else {
            for (const c of this.allCharts()) {
              if (c) c.setSelect({ left: 0, width: 0, top: 0, height: 0 }, false)
            }
          }
        }
        isDragging = false
      }
      this._mouseInside = false
      this._syncing = true
      for (const c of this.allCharts()) c?.setCursor({ left: -10, top: -10 }, false)
      this._syncing = false
      this.tempTooltip = null
      this.precipTooltip = null
      this.windTooltip = null
    })
  }

  override render() {
    if (this.sources.length === 0 || this.sources.every(s => s.hourly.length === 0)) {
      return html`<div class="no-data">No hourly data available</div>`
    }

    const areaHourly = this.primarySource?.hourly ?? []
    const lightningCells = areaHourly.map(h => {
      const risk = capeToLightningRisk(h.cape)
      const color =
        risk === 'high' ? '#ef4444' : risk === 'moderate' ? '#eab308' : 'rgba(255,255,255,0.05)'
      return html`<div class="lightning-cell" style="background: ${color}"></div>`
    })

    const showEnsembleLegend = this.ensembleSpreads.length > 0

    return html`
      <div class="legend">
        ${this.sources.map(
          s => html`
            <span class="legend-item">
              <span class="legend-swatch" style="background: ${s.color}"></span>
              ${s.label}
            </span>
          `
        )}
        ${showEnsembleLegend
          ? html`
              <span class="legend-item">
                <span
                  class="legend-swatch dashed"
                  style="--swatch-color: rgba(34,197,94,0.6)"
                ></span>
                Area median
              </span>
              <span class="legend-item">
                <span class="legend-swatch band" style="background: rgba(34,197,94,0.5)"></span>
                Area range
              </span>
            `
          : nothing}
      </div>
      <div class="charts-wrapper">
        <div class="chart-section">
          <span class="chart-label">Temperature</span>
          ${this.tempTooltip
            ? html`<div class="cursor-tooltip">${this.tempTooltip}</div>`
            : nothing}
          <div class="chart-temp" style="width:100%;height:100%"></div>
        </div>
        <div class="chart-section precip">
          <span class="chart-label">Precipitation</span>
          ${this.precipTooltip
            ? html`<div class="cursor-tooltip">${this.precipTooltip}</div>`
            : nothing}
          <div class="chart-precip" style="width:100%;height:100%"></div>
        </div>
        <div class="chart-section">
          <span class="chart-label">Wind</span>
          ${this.windTooltip
            ? html`<div class="cursor-tooltip">${this.windTooltip}</div>`
            : nothing}
          <div class="chart-wind" style="width:100%;height:100%"></div>
        </div>
        <div class="lightning-row">
          <span class="lightning-label">⚡</span>
          <div class="lightning-strip">${lightningCells}</div>
        </div>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'weather-day-chart': WeatherDayChart
  }
}
